<template>
    <div class="gap-10 px-5 py-5 grid min-h-screen grid-cols-2">
        <div>
            <p class="text-lg font-bold">
                {{ $t("workflows.email.settings_title") }}
            </p>

            <div class="mt-4 gap-1.5 grid w-full items-center">
                <Label for="email">{{
                    $t("workflows.email.automation_name")
                }}</Label>
                <Input
                    v-model="workflowCopy.name"
                    id="email"
                    type="email"
                    placeholder="Email"
                />
            </div>

            <!-- <div
                v-if="workflow.trigger === 'reservation_will_happen_in'"
                class="mt-4 grid w-full items-center gap-1.5"
            >
                <Label for="email">Odeslání před konáním rezervace</Label>
                <InputWithSelect :hide-intervals="['weeks']" v-model="test" />
            </div> -->

            <!-- <div class="mt-4 grid w-full items-center gap-1.5">
                <Label for="email">Odesílací doména</Label>
                <Input
                    disabled
                    :model-value="'support@zaptime.app'"
                    type="email"
                    placeholder="Email"
                />
            </div> -->

            <div class="gap-6 grid grid-cols-2">
                <div>
                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="email">{{
                            $t("workflows.email.recipient")
                        }}</Label>
                        <Select v-model:model-value="recieverId">
                            <SelectTrigger>
                                <SelectValue placeholder="Select a fruit" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem
                                        v-for="rec of recievers"
                                        :value="rec.id"
                                    >
                                        {{ rec.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="title">{{
                            t("workflows.email.title_label")
                        }}</Label>
                        <Input
                            v-model="title"
                            type="text"
                            :placeholder="
                                t('workflows.email.title_placeholder')
                            "
                        />
                    </div>

                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="subject">{{
                            t("workflows.email.subject_label")
                        }}</Label>
                        <Input
                            v-model="subject"
                            type="text"
                            :placeholder="
                                t('workflows.email.subject_placeholder')
                            "
                        />
                    </div>
                </div>

                <div class="mt-4">
                    <!-- Profile Photo File Input -->
                    <input
                        ref="photo"
                        type="file"
                        class="hidden"
                        @change="updatePhotoPreview"
                    />

                    <div class="mt-6 gap-4 flex">
                        <div
                            class="rounded-xl border-gray-200 bg-iron-50 p-4 flex w-full flex-col items-center justify-center border"
                            @dragover.prevent="dragOver = true"
                            @dragleave.prevent="dragOver = false"
                            @drop="handleDrop"
                            :class="[
                                dragOver
                                    ? 'shadow-lg ring-z-primary-600 ring-2'
                                    : '',
                            ]"
                        >
                            <Button
                                variant="outline"
                                size="icon"
                                class="mr-2"
                                type="button"
                                @click.prevent="selectNewPhoto"
                            >
                                <CloudUpload
                                    :size="20"
                                    class="text-iron-500"
                                ></CloudUpload>
                            </Button>

                            <p
                                class="mt-3 text-sm text-iron-500 max-w-[320px] text-center"
                            >
                                <Button
                                    type="button"
                                    @click.prevent="selectNewPhoto"
                                    variant="ghost"
                                >
                                    {{ $t("workflows.email.click_to_upload") }}
                                </Button>

                                {{ $t("workflows.email.email_image") }}
                            </p>

                            <p class="mt-2 text-xs text-iron-500 text-center">
                                {{ $t("workflows.email.image_format_info") }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-4 flex w-full">
                <div class="w-full">
                    <EmailEditor
                        @text-changed="onTextChangeDebounced"
                        class="mt-4"
                        v-model="emailContent"
                    ></EmailEditor>
                </div>
            </div>

            <div class="mt-6">
                <div class="flex items-center justify-between">
                    <Label>{{ t("workflows.email.attachments_title") }}</Label>
                    <span class="text-xs text-iron-500">
                        {{ formatFileSize(totalAttachmentSize) }} / 10 MB
                    </span>
                </div>

                <div class="mt-2 gap-2 flex flex-col">
                    <div
                        v-for="att of attachmentsList"
                        :key="att.id"
                        class="rounded-md border-gray-200 bg-iron-50 px-3 py-2 flex items-center justify-between border"
                    >
                        <div class="gap-2 flex items-center">
                            <FileText
                                v-if="att.mime_type === 'application/pdf'"
                                :size="16"
                                class="text-iron-500"
                            />
                            <ImageIcon
                                v-else
                                :size="16"
                                class="text-iron-500"
                            />
                            <span class="text-sm">{{ att.filename }}</span>
                            <span class="text-xs text-iron-400">
                                {{ formatFileSize(att.size) }}
                            </span>
                        </div>
                        <button
                            type="button"
                            class="text-iron-400 hover:text-red-500"
                            @click="removeAttachment(att.id)"
                        >
                            <X :size="16" />
                        </button>
                    </div>
                </div>

                <input
                    ref="attachmentInput"
                    type="file"
                    class="hidden"
                    accept=".pdf,.jpeg,.jpg,.png,.gif"
                    @change="handleAttachmentUpload"
                />

                <Button
                    variant="outline"
                    size="sm"
                    class="mt-2"
                    type="button"
                    @click="attachmentInput?.click()"
                    :disabled="attachmentUploading"
                >
                    <Paperclip :size="14" class="mr-1" />
                    {{ t("workflows.email.attachments_upload") }}
                </Button>

                <p class="mt-1 text-xs text-iron-500">
                    {{ t("workflows.email.attachments_format_info") }}
                </p>
            </div>
        </div>

        <div class="h-[calc(100vh-12rem)]">
            <iframe
                :srcdoc="emailPreview"
                class="rounded-sm border-gray-200 h-full w-full border"
                sandbox="allow-same-origin allow-scripts allow-modals allow-forms"
                frameborder="0"
                title="Email preview"
                data-gramm="false"
                data-gramm_editor="false"
                data-timer="false"
                data-gramm_id="false"
            >
            </iframe>
        </div>

        <!-- <div class="relative"> -->
        <!--     <div -->
        <!--         class="relative h-full w-full rounded-sm border border-gray-200" -->
        <!--     > -->
        <!--         <TopolEmailEditor /> -->
        <!--     </div> -->
        <!-- </div> -->
    </div>
</template>

<script setup lang="ts">
import type { Workflow } from "../Workflow.vue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ref, watch, computed } from "vue";
import { useFormattedSeconds } from "@/composables/useFormattedSeconds";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { InputWithSelect } from "@/components/ui/input-with-select";

const { convertSeconds } = useFormattedSeconds();

import { usePage } from "@inertiajs/vue3";
import EmailEditor from "./EmailEditor.vue";
import Button from "@/components/ui/button/Button.vue";
import axios from "axios";
import route from "ziggy-js";
import { useDebounceFn } from "@vueuse/core";
import { debouncedWatch } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useUrlSearchParams } from "@vueuse/core";
import { toast } from "@/components/ui/toast";
import {
    CloudUpload,
    FileText,
    ImageIcon,
    Paperclip,
    X,
} from "lucide-vue-next";
import { useToast } from "@/components/ui/toast/use-toast";
import { useEmailTemplateAttachments } from "@/composables/useEmailTemplateAttachments";
import type { EmailTemplateAttachmentDTO } from "@/types/EmailTemplateAttachments";

const { toast: toastNotification } = useToast();

// Map workflow triggers to email template types
function getEmailTemplateType(trigger: string): string {
    const mapping: Record<string, string> = {
        reservation_made: "reservation_made",
        reservation_will_happen_in: "reservation_reminder",
        reservation_canceled: "reservation_canceled",
        reservation_prepared: "reservation_confirmed",
        reservation_rescheduled: "reservation_rescheduled",
    };

    return mapping[trigger] || trigger;
}

// For image handling
const photo = ref();
const dragOver = ref(false);
const photoPreview = ref(null);
const fileForm = ref({
    image: null,
});

const params = useUrlSearchParams("history");

const page = usePage();
const { t } = useI18n();
const props = defineProps<{
    workflow: Workflow;
    template: any;
    emailHtmlContent: string;
    attachments?: EmailTemplateAttachmentDTO[];
}>();

const emit = defineEmits(["saved"]);

const emailPreview = ref(props.emailHtmlContent);
const workflowCopy = ref(props.workflow);
const emailContent = ref(
    convertMergeTagsToMentions(props.template.content_definition),
);
const title = ref(props.template.title);
const subject = ref(props.template.subject);

const recievers = [
    {
        id: "host",
        // @ts-expect-error
        label: t("your_email", { email: page.props.user.email }),
    },
    {
        id: "attendees",
        label: t("obtained_email"),
    },
];

const recieverId = ref(props.workflow.target);

// The attach_ics toggle is hidden for now: Google invitation emails are suppressed
// (sendUpdates 'none'), so the ICS is always attached and must not be opt-out.
const attachIcs = ref(props.workflow.attach_ics);

// Attachment handling
const { uploadAttachment, deleteAttachment } = useEmailTemplateAttachments();
const attachmentsList = ref<EmailTemplateAttachmentDTO[]>(
    props.attachments ?? [],
);
const attachmentInput = ref<HTMLInputElement>();
const attachmentUploading = ref(false);

const totalAttachmentSize = computed(() =>
    attachmentsList.value.reduce((sum, att) => sum + att.size, 0),
);

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

async function handleAttachmentUpload() {
    const file = attachmentInput.value?.files?.[0];
    if (!file) return;

    attachmentUploading.value = true;

    await uploadAttachment(props.template.id, file, {
        onSuccess: (attachment) => {
            attachmentsList.value.push(attachment);
        },
        onError: (error) => {
            toastNotification({
                title: t("error"),
                description:
                    error || t("workflows.email.attachments_upload_failed"),
                variant: "destructive",
            });
        },
    });

    attachmentUploading.value = false;
    if (attachmentInput.value) {
        attachmentInput.value.value = "";
    }
}

async function removeAttachment(id: number) {
    await deleteAttachment(id, {
        onSuccess: () => {
            attachmentsList.value = attachmentsList.value.filter(
                (att) => att.id !== id,
            );
        },
        onError: (error) => {
            toastNotification({
                title: t("error"),
                description:
                    error || t("workflows.email.attachments_delete_failed"),
                variant: "destructive",
            });
        },
    });
}

// Image handling functions
function selectNewPhoto() {
    photo.value?.click();
}

function updatePhotoPreview() {
    if (!photo.value?.files?.length) return;

    const file = photo.value.files[0];

    // Check if file size exceeds 1MB
    if (file && file.size > 1024 * 1024) {
        toastNotification({
            title: t("profile.image_validation.title"),
            description: t("profile.image_validation.description"),
            variant: "destructive",
        });

        photo.value.value = "";
        return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
        // @ts-expect-error
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(file);
    uploadEmailImage(file);
}

const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    dragOver.value = false;
    if (event.dataTransfer?.files.length) {
        const file = event.dataTransfer.files[0];

        // Check if file size exceeds 1MB
        if (file && file.size > 1024 * 1024) {
            toastNotification({
                title: t("profile.image_validation.title"),
                description: t("profile.image_validation.description"),
                variant: "destructive",
            });
            return;
        }

        uploadEmailImage(file);
    }
};

async function uploadEmailImage(file: File) {
    fileForm.value.image = file;

    try {
        // Create FormData
        const formData = new FormData();
        formData.append("image", file);
        formData.append("template_id", props.template.id);

        // Use axios to upload the image
        await axios.post(route("app.email-templates.images.store"), formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        // Refresh the email preview after uploading
        await renderPreview();
    } catch (error) {
        toastNotification({
            title: t("error"),
            description: t("email.image_upload_failed"),
            variant: "destructive",
        });
    }
}

async function onTextChange(text: string) {
    await onTemplateSave();
    await renderPreview();
}

function convertMergeTagsToMentions(html: string) {
    if (!html) return "";

    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Find all merge tag spans
    const mergeTagSpans = tempDiv.querySelectorAll(
        'span[data-type="merge-tag"]',
    );

    mergeTagSpans.forEach((span) => {
        const tagId = span.getAttribute("data-id");
        if (tagId) {
            // Replace the span with a mention node
            const mentionNode = document.createElement("span");
            mentionNode.setAttribute("data-type", "mention");
            mentionNode.setAttribute("data-id", tagId);
            mentionNode.className = "merge-tag";
            mentionNode.textContent = tagId;
            span.replaceWith(mentionNode);
        }
    });

    return tempDiv.innerHTML;
}
const onTextChangeDebounced = useDebounceFn(onTextChange, 350);

debouncedWatch(
    [title, subject],
    async () => {
        await onTemplateSave();
        await renderPreview();
    },
    { debounce: 350 },
);

debouncedWatch(
    [() => workflowCopy.value.name, recieverId, attachIcs],
    () => {
        onWorkflowSave();
    },
    { debounce: 350 },
);

async function onWorkflowSave() {
    await axios.put(
        route("app.workflows.update", { workflow: props.workflow.id }),
        {
            name: workflowCopy.value.name,
            target: recieverId.value,
            attach_ics: attachIcs.value,
        },
    );

    emit("saved");
}

async function renderPreview() {
    const preview = await axios.post(
        route("app.email-templates.previews.show", {
            emailTemplate: props.template.id,
        }),
        { withContent: true, eventTypeId: params["event-type"] },
    );

    emailPreview.value = preview.data.data.html;
}

async function onTemplateSave() {
    const response = await axios.put(
        route("app.email-templates.update", {
            emailTemplate: props.template.id,
        }),
        {
            type: getEmailTemplateType(props.workflow.trigger),
            title: title.value,
            subject: subject.value,
            content: emailContent.value,
        },
    );

    if (
        response.data.success === false &&
        response.data.message ===
            "You need to subscribe to Zaptime to use this feature."
    ) {
        toast({
            title: t("paid_features.title"),
            description: t("paid_features.email_template_save"),
            variant: "destructive",
        });
        return;
    }

    emit("saved");
}
</script>
